/**
 * `/reset-password?token=…` — set a new password from an emailed link.
 *
 * No captcha: the link is the scarce thing, it works once, and it was mailed to
 * an address somebody had to control. A puzzle on top would be friction with no
 * security in it.
 *
 * No automatic sign-in either. Completing this revokes every session on the
 * account — that is the point of resetting a password you believe is
 * compromised — and minting a fresh session straight from an emailed link would
 * undo it in the same breath. The person signs in with the password they just
 * chose, which also confirms they can remember it.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/api/client";

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    // Checked here and nowhere else: the second box exists to catch a typo in
    // the first, which is a browser-side concern. The API is never sent it.
    if (password !== confirmation) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.anonymousPost("/api/v1/auth/password-reset/confirm", { token, password });
      navigate("/signin", { replace: true, state: { passwordReset: true } });
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? (cause.fieldMessage ?? cause.message)
          : "Could not set the new password",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <main className="page container" style={{ display: "grid", placeItems: "center" }}>
        <div className="panel panel--narrow" style={{ marginTop: 80 }}>
          <h1 style={{ marginBottom: 6 }}>No reset link</h1>
          <p style={{ color: "var(--paper-300)", fontSize: 14, lineHeight: 1.6 }}>
            Open this page from the link in the email, or ask for a new one.
          </p>
          <Link className="btn btn--accent btn--block" to="/forgot-password" style={{ marginTop: 8 }}>
            Send a reset link
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page container" style={{ display: "grid", placeItems: "center" }}>
      <form className="panel panel--narrow" onSubmit={submit} style={{ marginTop: 80 }}>
        <h1 style={{ marginBottom: 6 }}>Choose a new password</h1>
        <p style={{ color: "var(--paper-400)", fontSize: 13, marginTop: 0, marginBottom: 22 }}>
          This signs out every device currently using the account.
        </p>
        {error && <div className="alert alert--error">{error}</div>}
        <div className="field">
          <label htmlFor="password">New password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <span style={{ fontSize: 12, color: "var(--paper-400)" }}>At least 10 characters.</span>
        </div>
        <div className="field">
          <label htmlFor="confirmation">Repeat the password</label>
          <input
            id="confirmation"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </div>
        <button className="btn btn--accent btn--block" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Set the new password"}
        </button>
        <p style={{ marginTop: 18, color: "var(--paper-400)", fontSize: 13 }}>
          <Link to="/signin" style={{ color: "var(--accent)" }}>Back to sign in</Link>
        </p>
      </form>
    </main>
  );
}
